<script lang="ts" setup>
    import '../../../css/choice.css';  
    import { ModalWindowOpen } from '../../lib/ChoiceUserConfiguration';
    import {onMounted,onBeforeMount,ref,watch,reactive, computed} from 'vue';  
    import {popAlert,ModalStyles} from '../../lib/Notification';     
    import {AppScreens,PrivilegeType,UserSettingGroup} from '../../Enums/ChoiceEnums';
    import {ModalSize,ButtonDisplayStyle,DropDownButtonIconType,IconTypes,DisplayStyle} from "../../Enums/UiEnums"; 
    import {ErrorReturnMessage,getCreditDetails,UpdatePatientCreditNotes,ICreditNotesRequest,IPatientCreditBalance} from '../../lib/Api/PatientCredit';
    import {IClaim,ClaimDisplay,claimLegacyWindowOpen,ClaimSimpleLists} from '../../lib/Models/ClaimModel'; 
    import {IPatientServicePatientHeaderResponse,UpdateTitleBar} from '../../lib/Models/PatientModel';
    import {prettyCurrency} from '../../lib/Formatting/Number'; 
    import TextInput from '../../components/TextInput.vue';
    import ModalDialog from '../../components/ModalDialog.vue'; 
    import ActionFoldingPanel from "../../components/ActionFoldingPanel.vue"; 
    import ClaimRefund from '../Billing/Subcomponents/ClaimRefund.vue';
    import ClaimTransfer from '../Billing/Subcomponents/ClaimTransfer.vue';
    import ClaimList from '../Billing/Subcomponents/ClaimList.vue';
    import DropDownButton from "../../components/DropDownButton.vue";
    import DropDownButtonItem from "../../components/DropDownButtonItem.vue";
    import Icon from "../../components/Icon.vue";  
    import {IActionButtons, ActionButtonStyle} from '../../lib/Types/Types';   
    import { initPinia } from '../../lib/PiniaMgr';
    import {useUserSettingsStore} from '../../lib/Stores/UserSettingsStore'; 
    import {useRefundUpdateStore} from '../../lib/Stores/RefundUpdateStore';
    import {useRefundFullfillmentStore} from '../../lib/Stores/RefundFullfillmentStore';
    import {useSimpleListStore} from '../../lib/Stores/SimpleListStore';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore'

    const actionButtonWidth = 150;
    const selectClaim = true;
    const userSettingsStore = useUserSettingsStore(initPinia());
    const refundUpdateStore = useRefundUpdateStore(initPinia());
    const refundFullfillmentStore = useRefundFullfillmentStore(initPinia());
    const simpleListStore = useSimpleListStore(initPinia());
    const privilegesStore = usePrivilegesStore(initPinia());
    interface ClaimCreditDisplaySetting {Procedures: boolean, Payments: boolean, Comments: boolean};
    interface ClaimDistributeSetting {Auto: boolean};

    enum PageActions{
        Audit,
        CompanySettings,
        Refresh 
    } 
    const sourceButtons = ref<IActionButtons>(
        [
            {baseText: 'Refund', disabled: true, clickEvent: refundOpen, toolTip: 'Refund selected items', width: actionButtonWidth, spinText: "Refunding", overRideStyle:ActionButtonStyle.HandDollar}
        ] 
    );  
    const destinationButtons = ref<IActionButtons>(
        [
            {baseText: 'Transfer', disabled: true, clickEvent: transferOpen, toolTip: 'Transfer selected items', width: actionButtonWidth, spinText: "Transferring", overRideStyle:ActionButtonStyle.Transfer}
        ] 
    ); 
    const formData = reactive <
    {   
        showTransfers: boolean,
        showRefunds: boolean,
        autoDistribute: boolean, 
        claimDisplay: {source: boolean, destination: boolean},  
        creditNotes: string,
        credNotesDisabled: boolean,
        creditAuditing: string,
        details?: IPatientCreditBalance,
        detailsClass: {credit: string, openBalance: string},
        creditClaimList?: IClaim[],
        openClaimList?: IClaim[],
        selectedSourcList?: [],
        selectedDestinationList?: [],
        refundList? : IClaim[],
        transferList? : IClaim[], 
        claimToggles: {procedures: boolean, payments: boolean, comments: boolean},
        claimToggles2: {procedures: boolean, payments: boolean, comments: boolean}
    }>({      
        showTransfers: false, 
        showRefunds: false, 
        autoDistribute: false,
        creditNotes: "",
        credNotesDisabled: false,
        creditAuditing: "",
        claimDisplay: {source: false, destination: false}, 
        detailsClass: {credit: "billing-cell billing-data-right", openBalance: "billing-cell billing-data-right"}, 
        claimToggles: {procedures: false, payments: false, comments: false},
        claimToggles2: {procedures: false, payments: false, comments: false},

    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        inputClaimId: number,
        patientResults? : IPatientServicePatientHeaderResponse
    }>(),
    {
        saveEnabled: 0
    });
    const updatePrivilege = computed<boolean>(() => privilegesStore.screens[AppScreens.CreditClaimSelect] >= PrivilegeType.ReadUpdate ? true : false);
    onBeforeMount(async () => {
        await privilegesStore.getScreens([AppScreens.CreditClaimSelect]);
        if(!updatePrivilege.value){
            formData.credNotesDisabled = !updatePrivilege.value;
        }
    }); 
    onMounted(async () => {
        getClaimSelectUserSettings();
        simpleListStore.getSimpleLists([ClaimSimpleLists.RefundPaymentType, ClaimSimpleLists.PaymentMethod]);
        refreshData();
    });  
    
    watch(() => refundUpdateStore.refund_changed, (newValue, oldValue) => { 
        if (newValue !== oldValue) { 
            formData.refundList = [];
        }
    });
    
    async function refreshData() : Promise<void>  {
        formData.refundList = [];
        formData.selectedSourcList = [];
        formData.transferList = [];
        formData.creditClaimList = [];
        formData.openClaimList = [];
        formData.selectedDestinationList = [];    
        ResetRefundTransferButtons();    
        if (!props.patientResults?.patientId) {  
            return;
        } 
        const respGet = await getCreditDetails(props.patientResults.patientId,props.inputClaimId); 
        if (!respGet.success) {  
            await popAlert(respGet.error?.error ?? ErrorReturnMessage.details,"Refresh Details",ModalStyles.danger);
            return;
        }    
        formData.details = respGet.success?.patientCreditInformation; 
        formData.creditNotes = respGet.success?.creditNotes?.notes ?? "";
        formData.creditAuditing = respGet.success?.creditNotes?.auditing ?? "";
        formData.detailsClass.credit = "billing-cell " + (formData.details.totalCreditBalance < 0 ? "isalus-negative-number" : "billing-data-right");
        formData.detailsClass.openBalance = "billing-cell " + (formData.details.totalOpenBalance < 0 ? "isalus-negative-number" : "billing-data-right");
        formData.creditClaimList = respGet.success.creditClaimList;
        formData.openClaimList = respGet.success.openClaimList;  
        formData.claimDisplay.source = (formData.creditClaimList.length > 0 ? true : false);
        formData.claimDisplay.destination = (formData.openClaimList.length > 0 ? true : false); 
        UpdateTitleBar(props.patientResults.patientId,props.patientResults.patientName,props.patientResults.birthDate ?? "");

        refundFullfillmentStore.refundPatientCreditBalance = 
            {
                patientID: props.patientResults.patientId, 
                patientName: props.patientResults.patientName, 
                patientTooltip: `Chart: ${props.patientResults.patientId}\r\nName: ${props.patientResults.patientName}\r\nGender: ${props.patientResults.genderDescription}\r\nAge: ${props.patientResults.patientAge}\r\nBirth Date: ${props.patientResults.birthDate}`, 
                patientBalance: (formData.details?.totalOpenBalance ?? 0)
            }
         
    }
    function openPatient() : void  { 
        if (!props.patientResults?.patientId) {  
            return;
        } 
        const patientURL = `/officemd/screens/WindowSetup.htm?WT=Patient Setup&Qualifier=patient&WindowID=${props.patientResults.patientId}&tab=Demographics`;
        claimLegacyWindowOpen("NewPatient", patientURL); 
    } 
    function openPatientHistory() : void  { 
        if (!props.patientResults?.patientId) {  
            return;
        } 
        const patientHistoryURL = `/officemd/screens/transactionHistory.asp?PID=${props.patientResults?.patientId}`
        claimLegacyWindowOpen("TransHistory", patientHistoryURL); 
    } 
    function refundOpen() : void {
        sourceButtons.value[0].executing = true;
        formData.showRefunds = true;
        formData.refundList = formData.selectedSourcList?.map(item => ({claimID: item}));
        setTimeout(() => {
            sourceButtons.value[0].executing = false;
        },150);
    }
    function transferOpen() : void {
        destinationButtons.value[0].executing = true;
        formData.showTransfers = true;
        formData.refundList = formData.selectedSourcList?.map(item => ({claimID: item}));
        formData.transferList = formData.selectedDestinationList?.map(item => ({claimID: item}));
        setTimeout(() => {
            destinationButtons.value[0].executing = false;
        },150);
    }

    // User Settings functions
    async function paymentCreditSetting() :  Promise<void>  {  
        if (!formData.claimToggles.procedures && formData.claimToggles.payments) {
            formData.claimToggles.procedures = true;
        }
        updateCreditSetting();
    }
    async function updateCreditSetting() :  Promise<void>  { 
        const new_value: ClaimCreditDisplaySetting = {"Procedures": formData.claimToggles.procedures, "Payments": formData.claimToggles.payments, "Comments": formData.claimToggles.comments};
        userSettingsStore.saveUserSetting(UserSettingGroup.ClaimCreditSettings, "DISP",  new_value); 
    } 
    async function paymentCreditSetting2() :  Promise<void>  {  
        if (!formData.claimToggles2.procedures && formData.claimToggles2.payments) {
            formData.claimToggles2.procedures = true;
        }
        updateCreditSetting2();
    }
    async function updateCreditSetting2() :  Promise<void>  { 
        const new_value: ClaimCreditDisplaySetting = {"Procedures": formData.claimToggles2.procedures, "Payments": formData.claimToggles2.payments, "Comments": formData.claimToggles2.comments};
        userSettingsStore.saveUserSetting(UserSettingGroup.ClaimCreditSettings, "DISP2",  new_value); 
    } 
    async function updateDistributeSetting() :  Promise<void>  { 
        const new_value: ClaimDistributeSetting = {"Auto": formData.autoDistribute};
        userSettingsStore.saveUserSetting(UserSettingGroup.ClaimCreditSettings, "DIST",  new_value); 
        refundFullfillmentStore.transferTotals.AutoDistribute = formData.autoDistribute;
        refundFullfillmentStore.transferAutoDistribute();
    }   
    function getClaimSelectUserSettings() :  void  {
        // Get user preferences
        userSettingsStore.getUserSettingObj<ClaimCreditDisplaySetting>(UserSettingGroup.ClaimCreditSettings, "DISP")
            .then(respUserPref => {
                if (undefined != respUserPref)
                {
                    formData.claimToggles.comments = respUserPref.Comments;
                    formData.claimToggles.payments = respUserPref.Payments;
                    if (respUserPref.Payments && !respUserPref.Procedures) { 
                        formData.claimToggles.procedures = true;
                    }
                    else { 
                        formData.claimToggles.procedures = respUserPref.Procedures;
                    } 
                }
            });
        userSettingsStore.getUserSettingObj<ClaimCreditDisplaySetting>(UserSettingGroup.ClaimCreditSettings, "DISP2")
            .then(respUserPref => {
                if (undefined != respUserPref)
                {
                    formData.claimToggles2.comments = respUserPref.Comments;
                    formData.claimToggles2.payments = respUserPref.Payments;
                    if (respUserPref.Payments && !respUserPref.Procedures) { 
                        formData.claimToggles2.procedures = true;
                    }
                    else { 
                        formData.claimToggles2.procedures = respUserPref.Procedures;
                    } 
                }
            });
        userSettingsStore.getUserSettingObj<ClaimDistributeSetting>(UserSettingGroup.ClaimCreditSettings, "DIST")
            .then(respUserPref => {
                if (undefined != respUserPref)
                {
                    formData.autoDistribute = respUserPref.Auto; 
                }
            });
    } 
    function creditSourceList (itemList : any) : void {
        formData.refundList = [];
        formData.selectedSourcList = itemList;
        if(updatePrivilege.value){
            sourceButtons.value[0].disabled = ((formData.selectedSourcList?.length ?? 0) === 0 ? true : false);
        }
        enableDestination();
    }
    function creditDestinationList (itemList : any) : void {
        formData.transferList = [];
        formData.selectedDestinationList = itemList;
        enableDestination();
    }
    function enableDestination() : void {
        if(!updatePrivilege.value){
            return;
        }
        destinationButtons.value[0].disabled = ((formData.selectedSourcList?.length ?? 0) > 0 && (formData.selectedDestinationList?.length ?? 0) > 0 ? false : true);
    }
    
    async function saveRefunds() : Promise<void> {
        formData.showRefunds = false;
        await refreshData();
    }
    async function saveTransfers() : Promise<void> {
        formData.showTransfers = false;
        await refreshData();
    }
    function ResetRefundTransferButtons() : void { 
        sourceButtons.value[0].disabled = true; 
        destinationButtons.value[0].disabled = true;
    }
    async function displayCompanySettings() : Promise<void> { 
        ModalWindowOpen({screenID: AppScreens.CreditRules, urlParmData: {PARM: "credt"}, vueModalOnly: true, callBack: refreshData}); 
    }
    async function displayAudit() : Promise<void> {   
        if (!props.patientResults?.patientId) {  
            return;
        } 
        ModalWindowOpen({screenID: AppScreens.Logging, urlParmData: {ScreenName: "CreditClaimSelect.cshtml", LogType: "s", StatementId: props.patientResults.patientId.toString()}, title: "Credit Balance Audit"}); 
    }
    
    async function saveCreditNotes() : Promise<void> {   
        if (!props.patientResults?.patientId) {  
            return;
        } 
       const messageTitle = "Save Patient Credit Notes";
       const updateRecord : ICreditNotesRequest = {PatientID: props.patientResults.patientId, Notes:  formData.creditNotes};
       
       const returnResults = await UpdatePatientCreditNotes(updateRecord);
        if (!returnResults.success) {  
            popAlert(returnResults.error?.error ?? ErrorReturnMessage.notes,messageTitle,ModalStyles.danger);
            return;
        } 
        if (returnResults.success.errorCode !== 0) { 
            popAlert(returnResults.success.errorMessage,messageTitle,ModalStyles.danger);
            return; 
        }
        formData.creditAuditing = returnResults.success.auditing;
    }
</script> 

<style scoped> 
    .viewDisplay {
        display: block;
    }
    .viewHide {
        display: none;
    }
    .cardActions {
        font-size: .85rem;
        font-weight: 500;
        color: #204D89;
    }
    .billing-header {
      color: #204D89;
      background-color: #f1f1f1;
    }
    .billing-data-left {
      color: black;
      background-color: white;
      text-align: left;
    }    
    .billing-data-center {
      color: black;
      background-color: white;
      text-align: center;
    }
    .billing-data-right {
      color: black;
      background-color: white;
      text-align: right;
    }
    .billing-cell {
      border: 1px solid #6A8CCB;
      FONT-SIZE: 8pt;
      font-family: Arial, sans-serif, Helvetica;
      padding: 5px 5px 5px 5px;
      cursor: default;
      vertical-align: middle;
    }
</style>
<template>
    <div class="row" >
        <div class="col-11">
            <ActionFoldingPanel
                panelTitle="Details" 
                actionTitle="Detail Actions"  
            > 
                <template #body>
                    <table border="0" aria-hidden="true" style="border: 1px solid blue; table-layout: fixed; border-collapse: collapse; width: 785px;">
                        <colgroup>
                            <col style="width: 90px" />
                            <col style="width: 90px" />
                            <col style="width: 90px" />
                            <col style="width: 90px" />
                            <col style="width: 405px" />
                        </colgroup>
                        <tr>
                            <td class="billing-header billing-cell">Patient</td>
                            <td colspan="3" class="billing-cell" >
                                <span class="isalus-link pe-2" @click="openPatient">
                                    {{props.patientResults?.patientId ?? 0}} 
                                </span>
                                <span :title="refundFullfillmentStore.refundPatientCreditBalance.patientTooltip">
                                    {{props.patientResults?.patientName ?? "Unknown"}}
                                </span>
                                <span class="text-danger ps-2" v-if="formData.details?.patientRHC">
                                    RHC: {{formData.details.patientRHC}}
                                </span>
                                <span class="ps-2">
                                    <Icon 
                                        :icon="IconTypes.Clock" 
                                        :displayStyle="DisplayStyle.Primary" 
                                        :class="'label-isalus-small text-isalus-primary'"
                                        :title="'Open Patient History'"
                                        @click="openPatientHistory"/>
                                </span>
                            </td>
                            <td rowspan="2" class="billing-cell">
                                <div >
                                    <TextInput 
                                        :rows=3
                                        :cssFramework="'vanilla'"  
                                        v-model:textValue="formData.creditNotes" 
                                        placeholder="Patient credit notes"
                                        @change:text-value="() => saveCreditNotes()" 
                                        :disabled="formData.credNotesDisabled"
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>           
                            <td class="billing-header billing-cell">Available Credit</td>
                            <td :class="formData.detailsClass.credit">
                                {{ prettyCurrency((formData.details?.totalCreditBalance ?? 0),{negativeCurrencyDisplay: true}) }}
                            </td>          
                            <td class="billing-header billing-cell">Open Balances</td>
                            <td :class="formData.detailsClass.openBalance">
                                {{ prettyCurrency((formData.details?.totalOpenBalance ?? 0),{negativeCurrencyDisplay: true}) }}
                            </td>        
                        </tr> 
                    </table>  
                    <div class="row" v-if="!formData.claimDisplay.source"> 
                        <div v-if="props.inputClaimId == 0" class="col text-danger label-isalus-small">
                            <br />This patient has no claims with credits and/or refunds.
                        </div>
                        <div v-if="props.inputClaimId != 0"  class="col text-danger label-isalus-small">
                            <br />Claim {{props.inputClaimId}} does not have a credit balance.
                        </div>
                    </div>   
                    <div class="row"> 
                        <div class="col-5"> 
                            <input id="tglDistribute" class="label-isalus-small" type="checkbox" @change=updateDistributeSetting()  v-model="formData.autoDistribute">
                             <label for="tglDistribute" class="ps-2 label-isalus-small">Auto Distribute when creating a Refund or Transfer.</label>
                        </div>   
                        <div v-if="formData.creditAuditing" class="col-5 fst-italic text-muted label-isalus-small">Credit notes: {{formData.creditAuditing}}</div>
                    </div>   
                </template> 
            </ActionFoldingPanel>  
        </div>
        <div class="col-1 text-end">
            <DropDownButton :display-style="ButtonDisplayStyle.Link" :buttonIcon="DropDownButtonIconType.Gear" >
                <DropDownButtonItem style="font-size: .75rem;" :data="PageActions.Audit" @click="displayAudit">Audit</DropDownButtonItem>
                <DropDownButtonItem style="font-size: .75rem;" :data="PageActions.CompanySettings" @click="displayCompanySettings">Company Settings</DropDownButtonItem>
                <DropDownButtonItem style="font-size: .75rem;" :data="PageActions.Refresh" @click="refreshData">Refresh</DropDownButtonItem> 
            </DropDownButton>
        </div>
    </div> 
    <div class="row" v-if="formData.claimDisplay.source"> 
        <ActionFoldingPanel
            panelTitle="Credit Sources" 
            actionTitle="Source Actions"  
            :actionList="sourceButtons"   
        > 
            <template #body>  
                <input id="tglProcedures" class="label-isalus-small" type="checkbox" @change=updateCreditSetting() v-model="formData.claimToggles.procedures">
                <label for="tglProcedures" class="ps-2 pe-2 label-isalus-small">Display Procedures</label>
                <input id="tglPayments" class="ps-2 label-isalus-small" type="checkbox" @change=paymentCreditSetting() v-model="formData.claimToggles.payments">
                <label for="tglPayments" class="ps-2 pe-2 label-isalus-small">Display Payments</label>
                <input id="tglComments" class="label-isalus-small" type="checkbox" @change=updateCreditSetting() v-model="formData.claimToggles.comments">
                <label for="tglComments" class="ps-2 label-isalus-small">Display Comments</label>
                <ClaimList 
                    :saveEnabled="props.saveEnabled"
                    :selection="selectClaim"
                    :claimList = "formData.creditClaimList"
                    :display="ClaimDisplay.CreditPayment"                 
                    :displayProcedures="formData.claimToggles.procedures"
                    :displayPayments="formData.claimToggles.payments"
                    :displayComments="formData.claimToggles.comments"
                    @selectedItems:model-value="creditSourceList" 
                /> 
            </template> 
        </ActionFoldingPanel>  
    </div>  
    <div class="row" v-if="formData.claimDisplay.source && formData.claimDisplay.destination"> 
        <ActionFoldingPanel
            panelTitle="Credit Destinations" 
            actionTitle="Destination Actions" 
            :actionList="destinationButtons"    
        > 
            <template #body>  
                <input id="tglProcedures2" class="label-isalus-small" type="checkbox" @change=updateCreditSetting2() v-model="formData.claimToggles2.procedures">
                <label for="tglProcedures2" class="ps-2 pe-2 label-isalus-small">Display Procedures</label>
                <input id="tglPayments2" class="ps-2 label-isalus-small" type="checkbox" @change=paymentCreditSetting2() v-model="formData.claimToggles2.payments">
                <label for="tglPayments2" class="ps-2 pe-2 label-isalus-small">Display Payments</label>
                <input id="tglComments2" class="label-isalus-small" type="checkbox" @change=updateCreditSetting2() v-model="formData.claimToggles2.comments">
                <label for="tglComments2" class="ps-2 label-isalus-small">Display Comments</label>
                
                <ClaimList 
                    :saveEnabled="props.saveEnabled"
                    :selection="selectClaim"
                    :claimList = "formData.openClaimList"
                    :display="ClaimDisplay.CreditHeader"                 
                    :displayProcedures="formData.claimToggles2.procedures"
                    :displayPayments="formData.claimToggles2.payments"
                    :displayComments="formData.claimToggles2.comments"
                    @selectedItems:model-value="creditDestinationList" 
                /> 
            </template> 
        </ActionFoldingPanel>  
    </div> 

    <ModalDialog 
        v-model:show="formData.showRefunds"
        :size="ModalSize.XL"
        :closeOnClickOutside="false"
        >
        <template #title>
            <div class="text-isalus-primary">Claim Refunds</div>
        </template> 
        <ClaimRefund 
            :saveEnabled="props.saveEnabled"
            :claimList="formData.refundList"
            :autodistribute="formData.autoDistribute"
            @saveRefunds="saveRefunds"
        /> 
    </ModalDialog> 

    <ModalDialog 
        v-model:show="formData.showTransfers"
        :size="ModalSize.XL"
        :closeOnClickOutside="false"
        >
        <template #title>
            <div class="text-isalus-primary">Claim Transfers</div>
        </template> 
        <ClaimTransfer
            :saveEnabled="props.saveEnabled"
            :claimRefundList="formData.refundList"
            :claimTransferList="formData.transferList"
            :autodistribute="formData.autoDistribute"
            @saveTransfers="saveTransfers"
        /> 
    </ModalDialog> 
</template>
